/**
 * Split long wrap-card copy into short readable beats for mobile.
 * Sentence ends (". Capital") and ";" / ·| separators become beats —
 * but honorifics (Mr., Dr., …), "Pvt." before "Ltd.", and single-letter
 * initials ("S. Aravindan") must not.
 *
 * Corporate suffixes like Ltd./Inc./Corp. are intentionally NOT masked so
 * real sentence breaks ("…Holdings Ltd. The board…") still split.
 */

/**
 * Periods that look like sentence ends but are titles / short forms.
 * Keep this list narrow — over-masking blocks legitimate beats.
 */
const TITLE_ABBREV =
  /\b(?:Mr|Mrs|Ms|Dr|Prof|Sr|Jr|Smt|Shri|Messrs|Capt|Col|Gen|Lt|Pvt|Vol|Fig|Nos|No|vs|etc|Approx|Rs|Re|e\.g|i\.e|M\/s)\./g;

/** Lone initials before a capitalized token: "S. Aravindan", "A. K. Singh". */
const INITIAL = /\b[A-Z]\./g;

const MASK = "\u0001";

const maskNonSentencePeriods = (text: string): string =>
  text
    .replace(TITLE_ABBREV, (m) => m.replace(/\./g, MASK))
    .replace(INITIAL, (m) => m.replace(/\./g, MASK));

const unmask = (text: string): string => text.split(MASK).join(".");

export const splitBeats = (text: string): string[] => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length < 90) return [cleaned];

  const masked = maskNonSentencePeriods(cleaned);
  // Do NOT split on ";" before earnings metrics — Results lines use
  // "Q1; Revenue …; PAT …" and must stay readable as one card.
  const parts = masked
    .split(
      /(?<=[.])\s+(?=[A-Z(])|(?<=;)\s+(?=(?!Consolidated|Standalone|Revenue|Total\s+income|Net\s+sales|Operating\s+revenue|Turnover|NII|Net\s+interest|PAT|PBT|EBITDA|EBIT|Net\s+profit|Net\s+loss|Profit|Loss)[A-Z(])|(?:\s+[·|]\s+)/
    )
    .map((p) => unmask(p).trim())
    .filter(Boolean);

  return parts.length > 1 ? parts : [cleaned];
};
